import type { CartLine } from "./store";
import {
  computeGst,
  gstPayableAddition,
  type GstBreakdown,
  type GstConfig,
} from "@/lib/tax/gst";


export const CURRENCY = "INR";
export const CURRENCY_LOCALE = "en-IN";

import { PROMO_CODES, promoDiscount, type AppliedPromo, type PromoCode } from "./promo";
export { PROMO_CODES, type PromoCode, type AppliedPromo };

/**
 * Customer-facing delivery charges configured in Admin → Settings → Shipping.
 * These are what the customer pays; courier (shipping partner) delivery-type
 * prices are internal logistics costs and are never used here.
 */
export type ShippingConfig = {
  /** Discounted subtotal at/above which Standard Delivery is free; 0 = no free delivery. */
  freeThreshold: number;
  standardFee: number;
  expressFee: number;
};

/** Reads the shipping charges from store settings; null when missing or invalid. */
export function shippingConfigFromSettings(
  settings: { shipping?: Partial<ShippingConfig> | null } | null | undefined,
): ShippingConfig | null {
  const s = settings?.shipping;
  if (!s) return null;
  const values = [s.freeThreshold, s.standardFee, s.expressFee].map(Number);
  if (values.some((v) => !Number.isFinite(v) || v < 0)) return null;
  const [freeThreshold, standardFee, expressFee] = values;
  return { freeThreshold, standardFee, expressFee };
}

/** True when the bag holds a pack whose own rule grants free Standard Delivery. */
export function hasFreeShippingPack(lines: Pick<CartLine, "pack">[]): boolean {
  return lines.some((l) => l.pack?.freeShipping === true);
}

/** True when the discounted subtotal qualifies for free Standard Delivery. */
export function qualifiesForFreeShipping(discountedSubtotal: number, config: ShippingConfig): boolean {
  return config.freeThreshold > 0 && discountedSubtotal >= config.freeThreshold;
}

export type PricingBreakdown = {
  subtotal: number;
  shipping: number;
  discount: number;
  total: number;
  freeShippingRemaining: number;
  /** Configured free Standard Delivery threshold (null when free delivery is off). */
  freeShippingThreshold?: number | null;
  /** Why Standard Delivery is free, when it is: store threshold or a pack-specific rule. */
  freeShippingReason?: "threshold" | "pack" | null;
  promo: AppliedPromo | null;
  /** GST split — present once a place of supply is known (checkout onwards). */
  gst?: GstBreakdown;
};

/** Place-of-supply context needed to calculate GST. */
export type GstContext = {
  config: GstConfig;
  state?: string | null;
  country?: string | null;
  customerGstin?: string | null;
  at?: Date;
};


export function formatPrice(amount: number): string {
  return new Intl.NumberFormat(CURRENCY_LOCALE, {
    style: "currency",
    currency: CURRENCY,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/** 
 * Calculates discounted price for a product based on its discountPercent.
 * Formula: originalPrice - (originalPrice * discountPercentage / 100)
 */
export function getDiscountedPrice(originalPrice: number, discountPercent: number | undefined): number {
  if (!discountPercent || discountPercent <= 0) return originalPrice;
  const discount = (originalPrice * discountPercent) / 100;
  return Math.round(originalPrice - discount);
}

export function calcSubtotal(lines: CartLine[]): number {
  return lines.reduce((s, l) => s + l.unitPrice * l.quantity, 0);
}

/** Built-in code lookup only; checkout uses server-validated promos instead. */
export function resolvePromo(code: string | null | undefined): PromoCode | null {
  if (!code) return null;
  return PROMO_CODES[code.trim().toUpperCase()] ?? null;
}

/**
 * Applies the GST split (when a place of supply is known) to an already
 * computed breakdown. Exclusive pricing adds the tax to the payable total;
 * inclusive pricing leaves the total untouched and only reports the split.
 */
export function applyGst(pricing: PricingBreakdown, ctx?: GstContext | null): PricingBreakdown {
  if (!ctx) return pricing;
  const gross = Math.max(0, pricing.subtotal - pricing.discount) + pricing.shipping;
  const gst = computeGst({
    gross,
    customerState: ctx.state ?? null,
    customerCountry: ctx.country ?? null,
    customerGstin: ctx.customerGstin ?? null,
    config: ctx.config,
    ...(ctx.at ? { at: ctx.at } : {}),
  });
  return { ...pricing, gst, total: gross + gstPayableAddition(gst) };
}

export function calcPricing(
  lines: CartLine[],
  appliedPromo: AppliedPromo | null | undefined,
  gstContext: GstContext | null | undefined,
  shippingConfig: ShippingConfig,
): PricingBreakdown {
  const subtotal = calcSubtotal(lines);
  const discount = promoDiscount(lines, appliedPromo);
  // A promo that no longer gives a discount (e.g. bag changed) is not shown as applied.
  const promo = appliedPromo && discount > 0 ? appliedPromo : null;
  const discounted = Math.max(0, subtotal - discount);
  // Bag view: Standard Delivery is assumed until a method is chosen at checkout.
  const freeShippingReason = lines.length === 0
    ? null
    : hasFreeShippingPack(lines)
      ? "pack"
      : qualifiesForFreeShipping(discounted, shippingConfig)
        ? "threshold"
        : null;
  const shipping = lines.length === 0 || freeShippingReason ? 0 : shippingConfig.standardFee;
  const total = discounted + shipping;
  const freeShippingThreshold = shippingConfig.freeThreshold > 0 ? shippingConfig.freeThreshold : null;
  const freeShippingRemaining =
    freeShippingThreshold === null || freeShippingReason === "pack"
      ? 0
      : Math.max(0, freeShippingThreshold - discounted);
  return applyGst(
    { subtotal, shipping, discount, total, freeShippingRemaining, freeShippingThreshold, freeShippingReason, promo },
    gstContext,
  );
}

